import type { CaptureResult, PostHog } from "posthog-js";
import { scrubPosthogProperties } from "./scrub-url";
import { onAnalyticsConsent, readAnalyticsConsent } from "../consent/analytics-consent";
import { forwardToMetaPixel } from "./meta-pixel";
import { forwardToOpenAiPixel } from "./openai-pixel";

/**
 * PostHog product analytics — the booking-funnel tracker.
 *
 * Same consent contract as Clarity (see ../consent/clarity-loader.ts): the
 * Worker exposes the project key as `window.__MJ_POSTHOG_KEY` without loading
 * anything, so the SSR HTML stays identical for every visitor (edge-cache
 * safe) and nothing is sent until the visitor explicitly accepts analytics.
 * The library itself is dynamic-imported so it never lands in the initial
 * bundle for visitors who decline.
 *
 * Events fired before the client is ready (first paint often beats the
 * import) are buffered in order and replayed once `init` resolves. The buffer
 * is capped so a visitor who never consents cannot grow it without bound.
 *
 * The `phc_` project key is a public write-only token by design; it is not a
 * secret in the way the server-side keys are.
 */

declare global {
  interface Window {
    __MJ_POSTHOG_KEY?: string;
    __MJ_POSTHOG_HOST?: string;
  }
}

/**
 * PostHog's managed reverse proxy for this project. Requests go to our own
 * domain (CNAME to PostHog's EU edge), so ad blockers that block
 * `*.posthog.com` no longer erase a chunk of the funnel. Data still lands in
 * the EU cloud project. Override with `VITE_POSTHOG_HOST` for a direct host.
 */
export const POSTHOG_DEFAULT_HOST = "https://a.majourneys.com";
export const POSTHOG_UI_HOST = "https://eu.posthog.com";

export type AnalyticsProps = Record<string, string | number | boolean | null | undefined>;

export type AnalyticsEvent =
  | "activity_viewed"
  | "package_selected"
  | "booking_details_started"
  | "checkout_viewed"
  | "booking_submitted"
  | "booking_completed"
  | "whatsapp_clicked"
  /* "Book on the app" promo: surface = popup | banner | home_section,
     store = ios | android (| other when the phone's store is unknown). */
  | "app_promo_shown"
  | "app_promo_closed"
  | "app_promo_store_click";

type Pending =
  | { kind: "capture"; event: AnalyticsEvent; props?: AnalyticsProps }
  | { kind: "identify"; id: string; props?: AnalyticsProps }
  | { kind: "reset" };

const MAX_PENDING = 20;

/**
 * Browsers raise "ResizeObserver loop completed with undelivered
 * notifications." (older Chrome: "ResizeObserver loop limit exceeded") when
 * a resize observation cannot settle within one frame and is deferred to the
 * next. It is a scheduling notice, not a thrown exception: nothing is
 * interrupted, nothing is skipped, and it carries no stack. PostHog's
 * exception autocapture still picks it up off the window `error` event and
 * files it as an unhandled error, which is how it surfaced as a "high" issue
 * from a single Firefox visit to checkout with no frame to act on. Sentry
 * ships the same two strings in its default ignore list for that reason.
 */
const BENIGN_BROWSER_NOTICES = [
  /^ResizeObserver loop completed with undelivered notifications\.?$/,
  /^ResizeObserver loop limit exceeded$/,
];

type ExceptionEntry = { value?: unknown };

/** `before_send`: returns null for exception events that are only browser noise. */
export function dropBenignBrowserNoise(cr: CaptureResult | null): CaptureResult | null {
  if (!cr || cr.event !== "$exception") return cr;
  const list = cr.properties?.$exception_list as ExceptionEntry[] | undefined;
  const values = Array.isArray(list)
    ? list.map((e) => e?.value).filter((v): v is string => typeof v === "string")
    : [];
  if (values.length === 0) return cr;
  const allNoise = values.every((v) => BENIGN_BROWSER_NOTICES.some((re) => re.test(v.trim())));
  return allNoise ? null : cr;
}

let client: PostHog | null = null;
let starting = false;
let pending: Pending[] = [];

function readKey(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const raw = window.__MJ_POSTHOG_KEY;
  return raw && /^[A-Za-z0-9_-]+$/.test(raw) ? raw : undefined;
}

function readHost(): string {
  if (typeof window === "undefined") return POSTHOG_DEFAULT_HOST;
  const raw = window.__MJ_POSTHOG_HOST?.trim();
  return raw && /^https:\/\/[A-Za-z0-9.-]+$/.test(raw) ? raw : POSTHOG_DEFAULT_HOST;
}

function apply(ph: PostHog, item: Pending): void {
  if (item.kind === "capture") ph.capture(item.event, item.props);
  else if (item.kind === "identify") ph.identify(item.id, item.props);
  else ph.reset();
}

function enqueue(item: Pending): void {
  if (client) {
    apply(client, item);
    return;
  }
  if (pending.length >= MAX_PENDING) return;
  pending.push(item);
}

async function start(key: string): Promise<void> {
  if (client || starting) return;
  starting = true;
  try {
    const { default: posthog } = await import("posthog-js");
    posthog.init(key, {
      api_host: readHost(),
      // Behind a proxy the SDK cannot infer the app URL for toolbar / replay
      // links, so point it at the EU cloud UI explicitly.
      ui_host: POSTHOG_UI_HOST,
      // Pin the SDK's config preset so a future posthog-js upgrade cannot
      // silently change capture behaviour underneath the explicit options.
      defaults: "2026-05-30",
      // TanStack Router drives navigation through the History API, so PostHog
      // can log every route change itself — no router subscription needed.
      capture_pageview: "history_change",
      capture_pageleave: true,
      // Clarity already records sessions; keep PostHog on events only.
      disable_session_recording: true,
      // Named funnel events only (track() below), so none of the per-click
      // collectors: each of them runs on every tap of every page, inside the
      // tap's own task, and adds to its INP (autocapture serialises the
      // element chain, heatmaps call getComputedStyle up the tree, dead clicks
      // watch the DOM after each click). They were on through the project's
      // remote settings, not by choice here.
      autocapture: false,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      rageclick: false,
      // Anonymous visitors stay cheap event-only rows; a person profile is
      // created only once identifyUser() runs (signed-in guests).
      person_profiles: "identified_only",
      // Exception autocapture follows the project's remote setting; keep
      // known browser noise from reaching Error tracking either way.
      // ...and never let a contact field or a full booking reference out in
      // a URL property (see scrub-url.ts).
      before_send: (cr) => scrubPosthogProperties(dropBenignBrowserNoise(cr)),
    });
    client = posthog;
    const replay = pending;
    pending = [];
    for (const item of replay) apply(posthog, item);
  } catch {
    // Ad blockers routinely kill the import. Analytics is never load-bearing.
    starting = false;
  }
}

/**
 * Boots PostHog when analytics consent is (or becomes) "granted". Safe to call
 * any number of times; only the first grant starts the client.
 */
export function loadPostHogIfConsented(): void {
  const key = readKey();
  if (!key) return;
  if (readAnalyticsConsent() === "granted") {
    void start(key);
    return;
  }
  const off = onAnalyticsConsent((value) => {
    if (value !== "granted") return;
    off();
    void start(key);
  });
}

/** Records a named funnel event. No-op (buffered) until consent + load. */
export function track(event: AnalyticsEvent, props?: AnalyticsProps): void {
  if (typeof window === "undefined") return;
  enqueue({ kind: "capture", event, props });
  // Ad attribution for Instagram / Facebook campaigns (see ./meta-pixel.ts).
  forwardToMetaPixel(event, props);
  forwardToOpenAiPixel(event, props);
}

/** Ties subsequent events to a signed-in user. Pass an opaque id, not an email. */
export function identifyUser(id: string, props?: AnalyticsProps): void {
  if (typeof window === "undefined" || !id) return;
  enqueue({ kind: "identify", id, props });
}

/** Test-only: drops the client and the buffer so each test starts cold. */
export function __resetForTests(): void {
  client = null;
  starting = false;
  pending = [];
}

/** Forgets the identified user (sign-out) so the next visitor starts fresh. */
export function resetUser(): void {
  if (typeof window === "undefined") return;
  enqueue({ kind: "reset" });
}
